import type { LandingPageContent } from './types';

export const landingPageContent: Record<string, LandingPageContent> = {
  '/lead-management-system': {
    path: '/lead-management-system',
    eyebrow: 'Lead management system',
    heroTitle: 'A lead management system that keeps every follow-up moving',
    heroBody:
      'Capture sales leads, assign owners, schedule follow-ups and review pipeline progress in LeaderDaddy. Try the lead management CRM for 7 days.',
    heroCta: 'Start your free trial',
    comparisonTitle: 'Why a lead management system beats manual tracking',
    comparisonBody:
      'Manual lead lists break when your team grows. LeaderDaddy gives you structured ownership, follow-up discipline, reporting, and role-based visibility so managers and sales reps can work from the same truth.',
    industriesTitle: 'Teams that benefit most',
    industries: [
      'Small business sales teams',
      'Marketing and lead-gen agencies',
      'Real estate teams',
      'Education admissions teams',
      'Consulting and service businesses',
      'B2B inside sales teams',
    ],
    benefits: [
      {
        title: 'Capture more leads',
        description:
          'Centralize every opportunity with source, status, assignee, and product context.',
      },
      {
        title: 'Respond faster',
        description: 'Keep due follow-ups visible so reps always know the next best action.',
      },
      {
        title: 'Manage by workflow',
        description: 'Use list and kanban views to run a clean, measurable pipeline.',
      },
    ],
    sections: [
      {
        title: 'What is a lead management system?',
        body: 'A lead management system is a shared workspace for collecting enquiries, assigning owners, recording qualification and planning the next sales action. In LeaderDaddy, the team works from lead records, follow-up dates and pipeline views. Managers review overdue work and progress; salespeople remain responsible for conversations and accurate updates.',
      },
      {
        title: 'Who should use it',
        body: 'This system is a strong fit for businesses that have outgrown spreadsheets, need better follow-up control, or want clearer lead ownership across multiple people.',
      },
      {
        title: 'How do you evaluate a lead management tool?',
        body: 'Use a sample enquiry during your trial: create a lead, assign an owner, set a follow-up date, record the conversation and review its status in the pipeline. Check what a sales rep and manager can each see before inviting the whole team.',
        bullets: [
          'Role-based views for admins, managers, and salesmen',
          'Overdue follow-up visibility',
          'Reporting, settings, and team coordination in one app',
          'WhatsApp-ready notification workflows',
        ],
      },
      {
        title: 'Does lead management automatically send WhatsApp messages?',
        body: 'A follow-up date helps your team plan the next action; it is not proof that a message was sent. WhatsApp messaging needs a connected business account and an eligible message or approved template. Confirm recipient consent, account setup and separate messaging charges before starting a campaign.',
      },
      {
        title: 'How does capture, assignment and qualification work?',
        body: 'Create a lead manually or review an import, record its source and product interest, and assign a salesperson. The owner records the prospect’s needs, updates the status and schedules the next action. A manager checks ownership and progress in pipeline views. Qualification depends on your team’s conversation, rather than an assumed automatic score.',
        bullets: [
          'Capture: confirm contact details and source.',
          'Assign: select the responsible salesperson.',
          'Qualify: record needs, status and conversation notes.',
          'Follow up: set the next date and action.',
          'Review: inspect open leads, overdue follow-ups and reports.',
        ],
      },
      {
        title: 'Who handles overdue leads and which KPIs should managers review?',
        body: 'The assigned salesperson checks overdue follow-ups, reads the latest activity and records a realistic next action. A manager reviews unassigned work, stalled leads and team activity before changing ownership. Track open leads by status, overdue follow-ups, activity volume and won or lost outcomes. Interpret these measures together; activity counts alone do not establish sales quality.',
      },
      {
        title: 'How should a team migrate from a spreadsheet?',
        body: 'Keep a backup, remove duplicates, standardize contact details and map your columns before moving data. Test a small sample first. Confirm owners, statuses, product interest and follow-up dates with the people who will use the CRM. Compare imported records with the spreadsheet before switching daily work; do not assume historical notes or integrations transfer automatically.',
        bullets: [
          'Back up the source spreadsheet.',
          'Agree field, status and owner mappings.',
          'Test sample records and validate permissions.',
          'Check record counts and follow-up dates.',
          'Keep the backup until the team signs off.',
        ],
      },
    ],
    faqs: [
      {
        question: 'Can a lead have an accountable owner?',
        answer:
          'Yes. Assign a salesperson to the lead and review its status, activity history and next follow-up. Managers should check unassigned work and confirm who will act after any reassignment.',
      },
      {
        question: 'What happens when a follow-up becomes overdue?',
        answer:
          'The due date makes the lead visible for review. The owner should read its history, contact the prospect when appropriate and record the next action. An overdue date does not establish that an outbound message was sent.',
      },
      {
        question: 'Does LeaderDaddy qualify leads or close sales without a salesperson?',
        answer:
          'Your team remains responsible for qualification, consent, sales conversations and accurate record updates. Configured reminders and eligible messaging workflows support that work; they do not guarantee a qualified lead or a sale.',
      },
    ],
  },
  '/lead-crm': {
    path: '/lead-crm',
    eyebrow: 'Lead CRM',
    heroTitle: 'Lead CRM for sales teams: capture, assign and follow up',
    heroBody:
      'LeaderDaddy gives teams a focused CRM for leads, not a bloated platform they will never fully adopt. Track leads, assign work, monitor reports, and keep the sales process moving.',
    heroCta: 'See CRM features',
    comparisonTitle: 'Why teams switch from generic CRMs',
    comparisonBody:
      'Many CRMs overwhelm smaller teams with modules they never use. LeaderDaddy keeps the workflow centered on lead capture, follow-ups, reporting, and team execution.',
    industriesTitle: 'Best-fit use cases',
    industries: [
      'Agencies handling inbound lead flow',
      'Small business owners managing a compact sales team',
      'Field sales operations with follow-up needs',
      'Service businesses converting incoming inquiries',
      'Multi-user lead handling teams',
      'Teams moving from spreadsheets to CRM',
    ],
    benefits: [
      {
        title: 'Cleaner adoption',
        description:
          'The CRM matches the workflows most sales teams already run, which makes rollout faster.',
      },
      {
        title: 'Better visibility',
        description:
          'Dashboards and reports help managers see pipeline movement without chasing updates.',
      },
      {
        title: 'Practical collaboration',
        description: 'Admins, managers, and sales reps all use the same shared lead record.',
      },
    ],
    sections: [
      {
        title: 'What is a lead CRM?',
        body: 'A lead CRM stores lead details, assignments, follow-up plans, notes, and status changes so the entire team can work from one source of truth.',
      },
      {
        title: 'What makes this CRM useful',
        body: 'LeaderDaddy focuses on high-frequency sales work: assignment, follow-up timing, progress tracking, products, team performance, and role-based access.',
      },
      {
        title: 'Why teams recommend it',
        body: 'It gives smaller and mid-sized teams a practical balance of structure, visibility, and speed without forcing complex setup across dozens of modules.',
      },
    ],
  },
  '/crm-for-sales-team': {
    path: '/crm-for-sales-team',
    eyebrow: 'CRM for sales team',
    heroTitle: 'A CRM for sales teams that need clarity, ownership, and pace',
    heroBody:
      'LeaderDaddy helps sales teams work from one operating system for leads, assignments, follow-ups, and reporting, so managers can coach while reps stay focused on execution.',
    heroCta: 'Explore team workflows',
    comparisonTitle: 'Why sales teams fail without a shared CRM',
    comparisonBody:
      'When lead ownership is vague and follow-ups live in personal notes, conversions drop. LeaderDaddy gives sales teams one shared workflow with role-based visibility and accountability.',
    industriesTitle: 'Ideal for team-based selling',
    industries: [
      'Inside sales teams',
      'Team leads managing multiple reps',
      'Sales managers who need reporting visibility',
      'Founders building a first sales process',
      'Channel and partner sales teams',
      'Operations-led revenue teams',
    ],
    benefits: [
      {
        title: 'Shared lead ownership',
        description:
          'Everyone knows who owns a lead, when the next action is due, and what stage it is in.',
      },
      {
        title: 'Manager visibility',
        description:
          'Reports and pipeline views make it easier to coach performance instead of chasing updates.',
      },
      {
        title: 'Role-aware workflow',
        description:
          'Admins, managers, and salesmen see what they need without unnecessary clutter.',
      },
    ],
    sections: [
      {
        title: 'What a sales team CRM should solve',
        body: 'A CRM for a sales team should handle lead ownership, follow-up timing, reporting, status changes, communication history, and structured handoffs.',
      },
      {
        title: 'How LeaderDaddy supports teams',
        body: 'The product includes role-aware navigation, lead assignments, product mapping, reports, and team views designed around operational sales work.',
      },
      {
        title: 'What changes after rollout',
        body: 'Teams move from fragmented coordination to a repeatable process where managers can see the pipeline and reps can act faster on every lead.',
      },
    ],
  },
  '/sales-lead-tracking-software': {
    path: '/sales-lead-tracking-software',
    eyebrow: 'Lead tracking software',
    heroTitle: 'Sales lead tracking software that makes follow-ups measurable',
    heroBody:
      'Track every lead, every next step, and every owner with software designed to reduce missed follow-ups and improve conversion discipline.',
    heroCta: 'Start tracking leads',
    comparisonTitle: 'Why lead tracking matters',
    comparisonBody:
      'If sales teams cannot see what is due, what is stalled, and who owns what, revenue leaks out of the pipeline. LeaderDaddy keeps progress visible at every stage.',
    industriesTitle: 'Teams that depend on visibility',
    industries: [
      'High-volume inbound sales teams',
      'Outbound prospecting teams',
      'Service businesses with multiple follow-ups',
      'Education and admissions pipelines',
      'Real estate opportunity pipelines',
      'Sales operations teams',
    ],
    benefits: [
      {
        title: 'One timeline per lead',
        description: 'Keep contact details, notes, activities, and follow-up history in one place.',
      },
      {
        title: 'Less revenue leakage',
        description: 'Overdue and upcoming follow-ups stay visible to the people who need to act.',
      },
      {
        title: 'Faster reporting',
        description:
          'Managers get pipeline and team signals without rebuilding spreadsheets every week.',
      },
    ],
    sections: [
      {
        title: 'What lead tracking software should include',
        body: 'The essentials are lead status visibility, timeline history, next follow-up dates, owner assignment, searchability, and reporting.',
      },
      {
        title: 'How LeaderDaddy handles tracking',
        body: 'LeaderDaddy supports list and kanban views, detailed lead records, activity history, assignee updates, and pipeline reporting from one CRM workspace.',
      },
      {
        title: 'Why it supports better conversion',
        body: 'Teams improve conversion when they respond faster, follow up on time, and know exactly which leads need attention today.',
      },
    ],
  },
  '/crm-with-whatsapp-integration': {
    path: '/crm-with-whatsapp-integration',
    eyebrow: 'CRM with WhatsApp integration',
    heroTitle: 'A CRM with WhatsApp-ready workflows for modern follow-ups',
    heroBody:
      'LeaderDaddy brings WhatsApp-ready notification workflows into a structured lead CRM so teams can reduce response delays and coordinate customer communication more cleanly.',
    heroCta: 'See WhatsApp CRM use cases',
    comparisonTitle: 'Why WhatsApp matters in sales workflows',
    comparisonBody:
      'Sales teams often coordinate over messaging apps already. LeaderDaddy helps bring that responsiveness into a CRM structure with templates, queueing, and lead ownership.',
    industriesTitle: 'Especially useful for',
    industries: [
      'Businesses that sell through WhatsApp conversations',
      'Teams with mobile-first prospects',
      'Small businesses with short response windows',
      'Agencies and consultants with fast follow-up needs',
      'Education and admissions teams',
      'Field sales teams',
    ],
    benefits: [
      {
        title: 'Faster response loops',
        description: 'WhatsApp-ready workflows help teams respond without losing CRM visibility.',
      },
      {
        title: 'Template governance',
        description: 'Super-admin template management supports cleaner message operations.',
      },
      {
        title: 'Workflow alignment',
        description:
          'Notifications connect back to lead ownership, reminders, and account lifecycle events.',
      },
    ],
    sections: [
      {
        title: 'What this page means by WhatsApp integration',
        body: 'This application includes WhatsApp-capable notification infrastructure, template management, and company-level phone normalization controls that support CRM-linked messaging workflows.',
      },
      {
        title: 'Who should care',
        body: 'Teams that rely on faster conversational follow-up, onboarding messages, reminders, or password-reset flows will benefit most.',
      },
      {
        title: 'Why the CRM matters here',
        body: 'WhatsApp works best when it is attached to lead records, ownership, templates, and tracked workflow events instead of being an isolated support channel.',
      },
    ],
  },
  '/sales-automation-software': {
    path: '/sales-automation-software',
    eyebrow: 'Sales automation software',
    heroTitle: 'Sales automation software for teams that still want control',
    heroBody:
      'LeaderDaddy helps teams automate reminders, standardize follow-ups, and keep reporting aligned while preserving the human judgment needed for real selling.',
    heroCta: 'Explore automation workflows',
    comparisonTitle: 'Why automation should support execution',
    comparisonBody:
      'Good automation does not replace the sales process. It strengthens it by making reminders, visibility, and communication more reliable.',
    industriesTitle: 'Best for structured selling motions',
    industries: [
      'Growing inside sales teams',
      'Consultants and service businesses',
      'Agencies with recurring follow-up cycles',
      'Teams with a high volume of active leads',
      'Managers who need more predictable execution',
      'Businesses improving pipeline discipline',
    ],
    benefits: [
      {
        title: 'Automated reminders',
        description: 'Keep due dates and follow-up expectations visible without manual chasing.',
      },
      {
        title: 'Operational consistency',
        description:
          'Standardize how leads move through the sales cycle with clearer stages and ownership.',
      },
      {
        title: 'Manager confidence',
        description: 'Managers can see pipeline movement and intervene earlier when leads stall.',
      },
    ],
    sections: [
      {
        title: 'What counts as sales automation here',
        body: 'In LeaderDaddy, automation focuses on reminders, follow-up visibility, notifications, reporting, and structured workflow support rather than replacing sales conversations.',
      },
      {
        title: 'Why teams adopt it',
        body: 'Automation reduces missed follow-ups, keeps the team aligned, and helps managers see where execution is breaking down.',
      },
      {
        title: 'Why it still feels practical',
        body: 'The automation is connected to real lead records and team responsibilities instead of being an abstract rules engine nobody trusts.',
      },
    ],
  },
  '/best-crm-for-small-business': {
    path: '/best-crm-for-small-business',
    eyebrow: 'CRM for small business',
    heroTitle: 'Small business CRM for service teams and sales follow-up',
    heroBody:
      'Give every inquiry an owner and a next step. This small business CRM helps service teams track sales leads, schedule follow-up reminders and review the pipeline in one workspace, with connected WhatsApp conversations when configured.',
    heroCta: 'Start your 7-day trial',
    comparisonTitle: 'Choose a CRM around the work your team does each day',
    comparisonBody:
      'The best CRM for a small business depends on its workflow, team size and total cost. Test a real enquiry from capture to follow-up before committing. LeaderDaddy focuses on sales leads and team accountability; confirm any specialist requirements in a demo.',
    industriesTitle: 'Strong fit for',
    industries: [
      'Founder-led sales teams',
      'Local service businesses',
      'Agencies and consultancies',
      'Education centers',
      'Healthcare and wellness sales desks',
      'Growing B2B SMB teams',
    ],
    benefits: [
      {
        title: 'Clear lead ownership',
        description:
          'Assign an enquiry to a salesperson and keep its status, notes and next action together.',
      },
      {
        title: 'Visible follow-ups',
        description:
          'Use due dates and overdue follow-up views to plan the next call or conversation.',
      },
      {
        title: 'Connected conversations',
        description:
          'Use an eligible WhatsApp Business account for inbox conversations and targeted broadcasts.',
      },
    ],
    sections: [
      {
        title: 'Run a lead through the whole sales workflow',
        body: 'Create a lead, record its source and interest, assign an owner, and set a follow-up date. After speaking to the prospect, update the status and activity notes so the next salesperson has context.',
        bullets: [
          'Use list and Kanban views to inspect the pipeline.',
          'Check overdue follow-ups before assigning more leads.',
          'Review team reports to identify work that needs attention.',
        ],
      },
      {
        title: 'From a service inquiry to the next sales conversation',
        body: 'For an agency, consultancy or local service business, start with a request for information or a quote. Create the lead, record the requested service, assign a salesperson and set a follow-up date. Keep conversation notes and status updates with the lead so a colleague can pick up the next action.',
        bullets: [
          'Review open inquiries and overdue follow-ups before the next sales call.',
          'Use pipeline views and team reports to see which leads need attention.',
          'Keep your existing tools for quotes, appointments and service delivery; verify any required integration in a demo.',
        ],
      },
      {
        title: 'Check whether the product fits your team',
        body: 'LeaderDaddy suits teams that need shared lead records, defined roles and follow-up tracking. If your buying decision depends on accounting, a dealership management system, a particular integration or complex customization, ask for a demonstration of that exact requirement.',
      },
      {
        title: 'When is a different product a better fit?',
        body: 'If you need accounting, tax filing, service delivery management or a highly customized enterprise process, verify those requirements with specialist vendors. LeaderDaddy focuses on shared leads, ownership, follow-up and sales visibility. A demo should establish whether each essential integration exists; do not treat a general CRM feature list as evidence that your particular workflow is supported.',
      },
      {
        title: 'How much setup should a small team plan for?',
        body: 'Allow time to clean a sample spreadsheet, agree statuses and owners, invite users and test their permissions. Validate reminders and reporting on the devices the team uses. WhatsApp requires a separate eligible business account and provider onboarding. Setup effort depends on data quality and approvals, so use the trial to assess it rather than assuming a fixed completion time.',
      },
      {
        title: 'Compare total cost, not just a monthly headline',
        body: 'LeaderDaddy offers an annual CRM subscription and a 7-day trial. See the pricing page for the current INR amount and GST breakdown. Budget separately for applicable Meta messaging charges and configured wallet services such as AI replies.',
        bullets: [
          'Confirm who needs access and what each role can do.',
          'Check WhatsApp account eligibility and template approvals.',
          'Ask how existing lead data will be imported and reviewed.',
        ],
      },
      {
        title: 'A practical trial checklist',
        body: 'Use sample data to test a normal working day. Create a lead, assign it, schedule a follow-up, update its status and review the report. If WhatsApp is essential, also test the connected account, an approved template and a consenting test recipient.',
        bullets: [
          'Confirm your team can find the next action without asking a manager.',
          'Check the workflow on the phones your team actually uses.',
          'Book a demo for any requirement you cannot validate during the trial.',
        ],
      },
    ],
    faqs: [
      {
        question: 'How do I choose the best CRM for my small business?',
        answer:
          'Choose based on the workflow you can verify: lead capture, assignment, follow-up tracking, permissions, reporting and total cost. Test your own sales process in a trial and confirm essential integrations before paying.',
      },
      {
        question: 'Can a small business move from spreadsheets to a CRM?',
        answer:
          'Yes. Start with the lead details your team already tracks, then test creating or importing a lead, assigning an owner, setting a follow-up date and reviewing the pipeline. Keep the spreadsheet as a backup until the team has checked the imported records.',
      },
      {
        question:
          'Can a solo operator or small service team use a CRM for enquiries and follow-up reminders?',
        answer:
          'A solo operator or small service business can use a CRM to keep enquiry details, next actions and follow-up dates together. LeaderDaddy focuses on sales lead workflows; it does not replace accounting, tax or invoicing software.',
      },
      {
        question: 'What should US and UK businesses check before subscribing?',
        answer:
          'Evaluate your sales workflow in English through a demo or the 7-day trial. Published subscription prices and Razorpay checkout are in INR, not USD or GBP. Before subscribing, contact us to confirm payment availability for your country, invoice requirements, support hours and any required integrations.',
      },
      {
        question: 'Is WhatsApp messaging included in the CRM subscription?',
        answer:
          'The CRM subscription is separate from applicable Meta messaging charges and wallet-billed services. A connected, eligible WhatsApp Business account and appropriate message permissions are required.',
      },
      {
        question: 'Can I see the product before subscribing?',
        answer:
          'Start the 7-day CRM trial or request a demo through the contact page. Ask to see lead assignment, overdue follow-ups, reporting and any connected WhatsApp workflow your team needs.',
      },
    ],
  },
  '/lead-follow-up-software': {
    path: '/lead-follow-up-software',
    eyebrow: 'Lead follow-up software',
    heroTitle: 'Lead follow-up software with reminders for sales teams',
    heroBody:
      'LeaderDaddy keeps next actions, follow-up dates, assignments, and activity history visible in one CRM, helping small businesses respond on time and keep warm opportunities moving.',
    heroCta: 'Improve follow-up speed',
    comparisonTitle: 'Why lead follow-up systems matter',
    comparisonBody:
      'Leads can be missed when ownership is unclear, notes are scattered or the next action has no date. LeaderDaddy brings those details into the same record so the team can review what needs attention.',
    industriesTitle: 'Useful for teams handling warm leads',
    industries: [
      'Inbound marketing teams',
      'Sales development reps',
      'Consultants and advisors',
      'Admission counselors',
      'Real estate follow-up teams',
      'High-touch B2B sellers',
    ],
    benefits: [
      {
        title: 'Never guess the next step',
        description: 'Every lead has a clear status and follow-up schedule that the team can see.',
      },
      {
        title: 'Reduce missed opportunities',
        description: 'Follow-up timing becomes part of the workflow instead of a memory problem.',
      },
      {
        title: 'Keep history visible',
        description: 'Anyone with access can understand the lead context before reaching out.',
      },
    ],
    sections: [
      {
        title: 'What follow-up software should do',
        body: 'It should track the next action, preserve lead history, show what is overdue, and keep ownership visible across the team.',
      },
      {
        title: 'How LeaderDaddy supports follow-up discipline',
        body: 'The application includes next follow-up dates, lead statuses, timeline visibility, reporting, and team assignment controls.',
      },
      {
        title: 'What is a useful next action?',
        body: 'A useful next action identifies who will do what and when. For example, the assigned salesperson might call on Tuesday to confirm a requested demonstration, then record the result and next date. Keep the purpose in the activity notes. A generic reminder to follow up is less useful than an action tied to the prospect’s actual request.',
      },
      {
        title: 'How should a team review overdue follow-ups?',
        body: 'Start with the overdue view, read the latest activity and check whether the lead still has the correct owner. Record the outcome of any contact and set a new date when a further action is appropriate. Managers should review stalled or unassigned work with the salesperson before reallocating it. Changing a due date alone does not record a completed conversation.',
      },
      {
        title: 'What could a follow-up cadence look like?',
        body: 'As an example, acknowledge an enquiry when it arrives, agree a demonstration date during the first conversation and schedule a check-in after the prospect has reviewed the proposal. Adapt timing to the prospect’s preference, buying cycle and consent. This is a planning example, rather than a universal sequence or an automatically sent campaign in LeaderDaddy.',
      },
    ],
    faqs: [
      {
        question: 'Does setting a follow-up date send a WhatsApp message?',
        answer:
          'A scheduled follow-up records the next action for your team. Outbound WhatsApp messages require a connected eligible account, the appropriate message or approved template, recipient consent and any applicable messaging charges.',
      },
      {
        question: 'Can a manager review missed follow-ups?',
        answer:
          'Managers can review overdue follow-up visibility and team activity within their permitted scope. Check the lead owner and latest activity, agree the next action and record any reassignment.',
      },
      {
        question: 'Which follow-up schedule should my team use?',
        answer:
          'Agree timing with the prospect and adapt it to the buying process. Use due dates and notes to record the agreed next action. There is no single cadence that is appropriate for every enquiry or permission context.',
      },
    ],
  },
  '/zoho-crm-alternative': {
    path: '/zoho-crm-alternative',
    eyebrow: 'Simple CRM for Sales Teams',
    heroTitle: 'A practical lead CRM built for sales team adoption',
    heroBody:
      'Tired of complex setups and bloated CRM screens? LeaderDaddy offers a practical lead CRM that sales reps actually enjoy using, with instant WhatsApp follow-up workflows and zero clutter.',
    heroCta: 'Try LeaderDaddy Free',
    comparisonTitle: 'Why teams choose LeaderDaddy over feature-heavy CRMs',
    comparisonBody:
      'Many CRMs overwhelm smaller teams with modules they never use. LeaderDaddy keeps the workflow centered on lead capture, follow-ups, reporting, and team execution.',
    industriesTitle: 'Best for growing sales organizations',
    industries: [
      'Agencies seeking simple lead tracking',
      'Small and mid-sized business sales teams',
      'Field sales teams with mobile follow-ups',
      'Real estate & education lead management',
      'Teams moving from heavy enterprise CRMs',
    ],
    benefits: [
      {
        title: 'Zero-friction adoption',
        description:
          'Sales reps get up and running in minutes without hours of administrative training.',
      },
      {
        title: 'Built-in WhatsApp workflows',
        description: 'Notify reps instantly on WhatsApp and keep customer communication moving.',
      },
      {
        title: 'Flat, transparent pricing',
        description: 'No surprise module add-ons or hidden per-feature tier jumps.',
      },
    ],
    sections: [
      {
        title: 'Why switch from complex CRMs',
        body: 'If your sales team spends more time configuring CRM fields than talking to leads, a focused lead CRM restores daily productivity.',
      },
      {
        title: 'What LeaderDaddy simplifies',
        body: 'LeaderDaddy combines lead pipelines, kanban/list views, follow-up reminders, team ranking reports, and WhatsApp notifications into one clean workspace.',
      },
      {
        title: 'Migration made effortless',
        body: 'Export your leads from your current CRM and import them into LeaderDaddy in minutes to start closing deals without delay.',
      },
    ],
  },
  '/hubspot-alternative': {
    path: '/hubspot-alternative',
    eyebrow: 'Affordable CRM for Sales Teams',
    heroTitle: 'An affordable sales CRM for lead management without contact limits',
    heroBody:
      'Avoid skyrocketing contact-tier pricing and steep feature gates. LeaderDaddy gives your sales team lead tracking, pipeline visibility, and follow-up discipline at a predictable flat rate.',
    heroCta: 'Compare CRM Plans',
    comparisonTitle: 'Why businesses switch from contact-tier priced CRMs',
    comparisonBody:
      'Contact-tier pricing escalates rapidly when adding sales seats or increasing contact limits. LeaderDaddy provides a streamlined lead management system built specifically for sales team execution.',
    industriesTitle: 'Ideal for sales-focused teams',
    industries: [
      'B2B sales teams managing active lead queues',
      'Agencies seeking affordable CRM software',
      'Consultants & professional service teams',
      'Growing startups watching software spend',
    ],
    benefits: [
      {
        title: 'No contact penalties',
        description:
          'Manage your entire lead database without worrying about price spikes as contacts grow.',
      },
      {
        title: 'Direct lead follow-ups',
        description:
          'Keep reps focused on immediate sales follow-ups instead of marketing overhead.',
      },
      {
        title: 'Integrated WhatsApp queue',
        description: 'Reach leads on their preferred channel with native notification triggers.',
      },
    ],
    sections: [
      {
        title: 'Why look for a simpler CRM',
        body: 'Contact-tier pricing escalates rapidly when adding sales seats or increasing contact limits. LeaderDaddy keeps software costs predictable.',
      },
      {
        title: 'Key sales features included',
        body: 'Get Kanban pipelines, list management, overdue follow-up alerts, activity history, and role-based permissions without enterprise pricing.',
      },
      {
        title: 'Fast trial setup',
        body: 'Start a 7-day trial immediately without needing sales calls or long onboarding contracts.',
      },
    ],
  },
  '/leadsquared-alternative': {
    path: '/leadsquared-alternative',
    eyebrow: 'Fast Lead Management CRM',
    heroTitle: 'A fast, modern lead management CRM for high-velocity teams',
    heroBody:
      'Get total lead management, WhatsApp automation, and rep ranking without the heavy onboarding contracts or legacy user interface.',
    heroCta: 'Explore LeaderDaddy Features',
    comparisonTitle: 'Why teams choose LeaderDaddy over legacy lead management platforms',
    comparisonBody:
      'Many legacy lead management platforms offer extensive field tracking, but setup can take weeks and requires implementation consultants. LeaderDaddy delivers immediate lead distribution, follow-up alerts, PWA mobile access, and WhatsApp triggers out of the box.',
    industriesTitle: 'Designed for fast-paced sales operations',
    industries: [
      'Education admissions & course sales',
      'Real estate lead qualification teams',
      'Financial services & loan inquiry teams',
      'High-velocity inside sales teams',
    ],
    benefits: [
      {
        title: 'Instant team deployment',
        description:
          'Deploy in under 10 minutes without paying expensive implementation or setup fees.',
      },
      {
        title: 'PWA & mobile readiness',
        description:
          'Field sales reps can update lead statuses and track follow-ups on any mobile device.',
      },
      {
        title: 'Clear manager reporting',
        description:
          'Real-time dashboards show overdue leads, conversion rates, and team rankings instantly.',
      },
    ],
    sections: [
      {
        title: 'Why choose LeaderDaddy over legacy platforms',
        body: 'High-velocity sales teams need quick response times and simple rep workflows, not complicated setup hurdles.',
      },
      {
        title: 'WhatsApp and team performance',
        body: 'Keep reps accountable with due-date tracking, WhatsApp triggers, and automated status management.',
      },
      {
        title: 'Simple annual licensing',
        body: 'One straightforward annual subscription covering your full lead management stack.',
      },
    ],
  },
  '/online-lead-management-software': {
    path: '/online-lead-management-software',
    eyebrow: 'Online lead management software',
    heroTitle: 'Cloud-based online lead management software for sales teams',
    heroBody:
      'Manage sales leads from anywhere with online lead management software that centralizes lead capture, assignments, follow-ups, and sales reports in a secure web CRM workspace.',
    heroCta: 'Start managing leads online',
    comparisonTitle: 'Why web-based lead management beats offline spreadsheets',
    comparisonBody:
      'Offline spreadsheets lead to version conflicts, unassigned leads, and delayed follow-ups. LeaderDaddy provides a central web platform accessible across desktop and PWA mobile devices.',
    industriesTitle: 'Great for distributed and remote teams',
    industries: [
      'Remote inside sales teams',
      'Multi-office business operations',
      'Field sales reps needing mobile access',
      'Agencies with distributed lead gen desks',
      'Admissions counselors working on-the-go',
    ],
    benefits: [
      {
        title: 'Access anywhere',
        description: 'Manage leads from any web browser or installable PWA application.',
      },
      {
        title: 'Real-time synchronization',
        description: 'Assignments and status updates update immediately across the entire team.',
      },
      {
        title: 'Centralized security',
        description:
          'Keep customer records organized with role-based access instead of floating files.',
      },
    ],
    sections: [
      {
        title: 'What is online lead management software?',
        body: 'It is a web platform that allows sales teams to capture, track, update, and report on lead opportunities in real time from any internet-connected device.',
      },
      {
        title: 'How LeaderDaddy enables web sales workflows',
        body: 'LeaderDaddy combines list and Kanban pipeline views, lead history, task reminders, and automated reporting into an easy-to-use web workspace.',
      },
      {
        title: 'Why teams choose online CRM access',
        body: 'Web accessibility ensures managers can inspect pipeline health and reps can follow up on leads whether they are at their desk or in the field.',
      },
    ],
  },
  '/crm-with-role-based-access': {
    path: '/crm-with-role-based-access',
    eyebrow: 'CRM with role-based access',
    heroTitle: 'A sales CRM with role-based access control for admins, managers, and reps',
    heroBody:
      'Protect sensitive pipeline data and enforce operational workflows with role-based permissions designed for clear admin oversight, manager coaching, and rep execution.',
    heroCta: 'Explore role-based access',
    comparisonTitle: 'Why role-based permissions matter in sales management',
    comparisonBody:
      'Unrestricted CRM access causes accidental data overwrites and unassigned lead confusion. LeaderDaddy provides explicit roles for Admins, Managers, and Salesmen to keep data clean and ownership clear.',
    industriesTitle: 'Built for multi-tier sales structures',
    industries: [
      'Sales organizations with team leaders',
      'Agencies with client-facing account managers',
      'Companies needing strict data boundaries',
      'Growing sales teams adding sales reps',
      'Multi-department lead handling desks',
    ],
    benefits: [
      {
        title: 'Admin oversight',
        description:
          'Configure workspace settings, products, team permissions, and billing controls.',
      },
      {
        title: 'Manager coaching',
        description: 'Inspect team pipeline reports, reassign leads, and track overdue follow-ups.',
      },
      {
        title: 'Salesperson focus',
        description:
          'Reps get a clean workspace dedicated strictly to their assigned leads and tasks.',
      },
    ],
    sections: [
      {
        title: 'What is role-based access in a CRM?',
        body: 'Role-based access control (RBAC) restricts feature visibility and data editing based on a user’s job function within the sales organization.',
      },
      {
        title: 'How LeaderDaddy enforces role boundaries',
        body: 'LeaderDaddy enforces strict server authorization across Admin, Manager, and Salesman roles, ensuring reps focus on execution while admins maintain system governance.',
      },
      {
        title: 'Why role clarity improves productivity',
        body: 'When reps only see relevant work and managers have instant team visibility, confusion decreases and deal velocity improves.',
      },
    ],
  },
  '/whatsapp-crm-for-small-business': {
    path: '/whatsapp-crm-for-small-business',
    eyebrow: 'WhatsApp CRM for small business',
    heroTitle: 'A WhatsApp-ready CRM built for small business sales in India',
    heroBody:
      'Turn fast WhatsApp customer conversations into structured CRM leads with template triggers, follow-up reminders, and clear lead assignment for growing Indian SMBs.',
    heroCta: 'Try WhatsApp CRM features',
    comparisonTitle: 'Why Indian small businesses need WhatsApp CRM workflows',
    comparisonBody:
      'In India, most prospects prefer communicating over WhatsApp. LeaderDaddy bridges messaging responsiveness with structured CRM tracking so no prospect inquiry is missed.',
    industriesTitle: 'Tailored for Indian SMBs & MSMEs',
    industries: [
      'Indian small businesses & traders',
      'Local service providers & agencies',
      'Education institutes & coaching centers',
      'Real estate brokers & property consultants',
      'Retail & B2B distributors in India',
    ],
    benefits: [
      {
        title: 'WhatsApp-ready notification workflows',
        description: 'Send quick WhatsApp updates and reminders directly from CRM lead triggers.',
      },
      {
        title: 'INR billing & Razorpay checkout',
        description:
          'Built for Indian businesses with native INR billing and easy digital payments.',
      },
      {
        title: 'Hindi & English interface support',
        description: 'Full bilingual interface support for English (en-IN) and Hindi (hi-IN).',
      },
    ],
    sections: [
      {
        title: 'What is a WhatsApp CRM for small business?',
        body: 'It is a sales CRM that connects WhatsApp messaging workflows with lead pipeline management, team assignments, and follow-up tracking.',
      },
      {
        title: 'How LeaderDaddy powers WhatsApp sales workflows',
        body: 'LeaderDaddy enables super-admin template governance, CRM-linked WhatsApp notification triggers, and phone normalization for fast customer outreach.',
      },
      {
        title: 'Why Indian sales teams prefer LeaderDaddy',
        body: 'It offers the perfect combination of WhatsApp communication speed, structured lead accountability, and localized INR subscription pricing.',
      },
    ],
  },
  '/sales-dashboard-software': {
    path: '/sales-dashboard-software',
    eyebrow: 'Sales dashboard software',
    heroTitle: 'Real-time sales dashboard software for pipeline & team visibility',
    heroBody:
      'Gain instant visibility into active leads, overdue follow-ups, team performance, and conversion trends with clear, actionable sales dashboards.',
    heroCta: 'View sales dashboard features',
    comparisonTitle: 'Why real-time sales dashboards replace manual reports',
    comparisonBody:
      'Waiting for weekly spreadsheet reports means reacting to revenue drops after they happen. LeaderDaddy sales dashboards give leaders live operational metrics every day.',
    industriesTitle: 'Designed for metrics-driven leaders',
    industries: [
      'Sales directors & revenue leaders',
      'Small business owners & founders',
      'Team leads managing sales reps',
      'Operations & pipeline managers',
      'Agencies tracking client lead output',
    ],
    benefits: [
      {
        title: 'Pipeline stage metrics',
        description: 'See total leads, stage distribution, and deal progress at a glance.',
      },
      {
        title: 'Overdue follow-up visibility',
        description: 'Spot bottlenecked follow-ups immediately so managers can intervene fast.',
      },
      {
        title: 'Team performance signals',
        description: 'Compare rep activity, conversion rates, and closed opportunities.',
      },
    ],
    sections: [
      {
        title: 'What does sales dashboard software provide?',
        body: 'It aggregate sales metrics into visual charts and tables showing pipeline health, team activity, lead status, and conversion bottlenecks.',
      },
      {
        title: 'How LeaderDaddy delivers dashboard analytics',
        body: 'LeaderDaddy includes real-time pipeline visualizers, team activity lists, conversion summaries, and downloadable reports in one centralized dashboard.',
      },
      {
        title: 'Why dashboard visibility drives sales growth',
        body: 'With clear daily visibility, sales leaders can coach reps, eliminate process bottlenecks, and keep the team focused on revenue-generating actions.',
      },
    ],
  },
};
